<template>
  <fn-form-field
    :id="id"
    :value="model"
    :name="name"
    :label="label"
    :rules="rules"
    :float-label="floatLabel"
    :emit-focus="emitFocus"
    :validation-mode="validationModeComputed"
    :hide-required-asterix="hideRequiredAsterix"
    :auto-complete-options="autoCompleteOptions"
    :type="type"
    :vee-type="veeType"
    :hide-errors="hideErrors"
    :class="{
      disabled: disabled,
      'ffi--show-remaining-length': showRemainingLength,
    }"
  >
    <template #default="slotProps">
      <fn-input
        v-bind="bindings"
        ref="input"
        :name="slotProps.field.name"
        :aria-label="ariaLabel || id"
        :value="slotProps.field.value"
        :has-prefix="!!inlinePrefix"
        :autocomplete="autocompleteComputed"
        @blur="onBlur($event, slotProps)"
        @focus="onFocus($event, slotProps)"
        @beforeinput="onBeforeInput"
        @keydown="onKeydown"
        @update:value="onInput($event, slotProps)"
      />
      <slot
        name="reset"
        v-bind="slotProps"
      />
      <slot
        name="insideInput"
        v-bind="slotProps"
      >
        <div
          v-if="inlinePrefix"
          class="inline-prefix"
        >
          {{ prefixText }}
        </div>

        <fn-anchor
          v-if="actionIcon && !actionIconOutside"
          id="formFieldInput-button"
          class="action-icon"
          :submit="actionIconSubmits"
          :aria-label="actionIconAriaLabel ? actionIconAriaLabel : undefined"
          role-button
          @click="$emit('actionIconClick', $event)"
          @keydown.enter="$emit('actionIconClick', $event)"
          @keydown.space.prevent="$emit('actionIconClick', $event)"
        >
          <fn-icon
            :icon="actionIcon.icon"
            :color="actionIcon.color"
            :size="actionIcon.size"
            role="presentation"
          />
        </fn-anchor>

        <fn-button
          v-if="actionIcon && actionIconOutside"
          id="formFieldInput-button"
          class="outside-button"
          :submit="actionIconSubmits"
          icon
          secondary
          :aria-label="actionIconAriaLabel ? actionIconAriaLabel : undefined"
          @click="$emit('actionIconClick', $event)"
        >
          <fn-icon
            :icon="actionIcon.icon"
            :color="actionIcon.color"
            :size="actionIcon.size"
            role="presentation"
          />
        </fn-button>

        <fn-button-round
          v-if="inlineSubmitButton && !actionIcon && falseOnServer(slotProps.field.meta.valid)"
          id="formFieldInput-submit"
          submit
          :aria-label="$t('accessibility.form.btnSubmit')"
          class="inline-button"
        >
          <fn-icon
            icon="arrow-right"
            color="white"
            size="xxs"
            role="presentation"
          />
        </fn-button-round>

        <p
          v-if="showRemainingLength"
          class="input-length-wrap"
        >
          <fn-icon
            :icon="'circle-i-light'"
            :size="'xs'"
            role="presentation"
          />
          <span class="flfpn-input-length"> {{ slotProps.field.value?.length || 0 }}/{{ maxlengthComputed }} </span>
        </p>
      </slot>
    </template>
    <template #afterLabel="slotProps">
      <slot
        name="afterLabel"
        v-bind="slotProps"
      />
    </template>
    <template #errors="slotProps">
      <slot
        name="errors"
        v-bind="slotProps"
      />
    </template>
  </fn-form-field>
</template>

<script setup lang="ts">
import { defineFormFieldPropsDefaults, type FormFieldProps } from '@/mixins/FormField';
import { defineInputBindings } from '@/mixins/InputFieldAttributes';
import type { HTMLInputEvent } from '@/types/forms';

type FormFieldInputProps = FormFieldProps & {
  inlineSubmitButton?: boolean;
  actionIcon?: { icon: string; color: string; size: string };
  actionIconSubmits?: boolean;
  actionIconAriaLabel?: boolean;
  inlinePrefix?: boolean;
  prefixText?: string;
  emitFocus?: boolean;
  actionIconOutside?: boolean;
  hideErrors?: boolean;
  showRemainingLength?: boolean;
  ariaLabel?: string;
};

const emit = defineEmits<{
  (name: 'keydown', value: KeyboardEvent): void;
  (name: 'focus', value: FocusEvent): void;
  (name: 'blur', value: FocusEvent): void;
  (name: 'beforeinput', value: HTMLInputEvent): void;
  (name: 'actionIconClick', value: PointerEvent): void;
}>();

const props = withDefaults(defineProps<FormFieldInputProps>(), {
  ...defineFormFieldPropsDefaults(),
  actionIcon: undefined,
  prefixText: '',
  ariaLabel: undefined,
});

const model = defineModel<string>('value', { default: '' });
const bindings = defineInputBindings(props);

const autocompleteStore = useAutocompleteStore();
const debounceFn = ref<Function>();

// Get max length, either from the validation rules
const maxlengthComputed = computed(() => (props.rules?.max as any)?.length || props.rules?.max || props.maxlength);
const validationModeComputed = computed(() => (props.autoCompleteOptions ? 'passive' : props.validationMode));
const autocompleteComputed = computed(() =>
  props.autoCompleteOptions && autocompleteStore.getAutoCompleteEnabled
    ? 'prevent-browser-autofill'
    : props.autocomplete,
);

const onFocus = (event: FocusEvent, slotProps: any) => {
  slotProps.handleFocus(event);
  emit('focus', event);
};

const onBlur = (event: FocusEvent, slotProps: any) => {
  slotProps.handleBlur(event);
  emit('blur', event);
};

const onBeforeInput = (event: HTMLInputEvent) => {
  emit('beforeinput', event);
};

const onKeydown = (event: KeyboardEvent) => {
  emit('keydown', event);
};

// Handle sanitization
const onInput = async (value: string, slotProps: any) => {
  model.value = props.bypassSanitation ? value : sanitizeTextInput(value, props.expandedSanitation);

  if (typeof debounceFn.value === 'function') {
    debounceFn.value(value);
  } else {
    debounceFn.value = useDebounceFn((debounceFnValue) => {
      slotProps.handleChange(debounceFnValue);
    }, 300);
    debounceFn.value(value);
  }
};

const falseOnServer = (condition: any) => (import.meta.client ? condition : false);
</script>

<style lang="scss" scoped>
.form-field {
  .inline-button {
    position: absolute;
    top: 0;
    right: $pad * 0.5;
    bottom: 0;
    margin: auto 0;
  }

  .action-icon {
    position: absolute;
    top: 0;
    right: $pad;
    bottom: 0;
    margin: auto 0;
    line-height: 40px;

    &:focus-visible {
      @include focus-outline-layered-circle-icon;
    }
  }

  .inline-prefix {
    display: none;
  }

  &.focused,
  &.populated {
    .inline-prefix {
      display: block;
      position: absolute;
      top: 0;
      left: $pad;
      bottom: 0;
      margin: auto 0;
      line-height: 40px;
      color: palette(grey, metallic);
    }
  }

  :deep(.form-input) {
    display: flex;
    column-gap: $pad * 0.5;
  }

  .input-length-wrap {
    position: absolute;
    top: 50px;
    display: flex;
    font-size: map-get($font-sizes, 's');
    line-height: 16px;
    color: palette(grey, base);

    .input-length {
      position: absolute;
      left: $pad;
    }
  }

  .flfpn-input-length {
    margin-left: ($pad * 0.25);
  }

  .outside-button {
    width: 40px;
  }

  &.ffi--show-remaining-length {
    margin-bottom: $pad * 1.25;
  }
}
</style>
